import React, {
  useState,
  useEffect,
  useCallback,
  Fragment,
  useContext,
  useMemo,
} from 'react';
import PropTypes from 'prop-types';
import UserProp from '../../../../commonComponents/propTypes/userProp';
import translate from '../../../../commonComponents/translate';
import Form from '../../../../commonComponents/forms/Form';
import FormFooter from '../../../../commonComponents/forms/FormFooter';
import FormSubtitle from '../../../../commonComponents/forms/FormSubtitle';
import ProfileField from './profile/ProfileField';
import AddAddressModal from '../../../sessions/modal/AddAddressModal';
import CountryProp from '../../../../commonComponents/forms/Address/CountryProp';
import { useModal } from '../../../../services/customHooks';
import Anchor from '../../../../commonComponents/Anchor';
import RemoveModal from '../components/RemoveModal';
import FormLabel from '../../../../commonComponents/forms/FormLabel';
import FormGroup from '../../../../commonComponents/forms/FormGroup';
import VatTypesProp from '../../../../commonComponents/forms/Address/VatTypesProp';
import {
  handleShippingAddressInOrder,
  PROFILE_BLOCK,
} from '../../../../services/utilityFunctions';
import LoggedUserContext from '../../../../contextProviders/LoggedUserContext';
import SkipStepButton from '../../../journey/overall/content/components/SkipStepButton';
import ConfigsContext from '../../../../contextProviders/ConfigsContext';
import {
  checkIfHasErrors,
  getErrors,
  getValidationRules,
} from '../../helpers/helper';
import ErrorMessage from '../../../../commonComponents/forms/ErrorMessage';

function stateFromProps(member = {}) {
  return {
    address: member.address,
    phone: member.phone || '',
    shippingAddress: member.shippingAddress || [],
  };
}

/**
 * Form to update the current logged user
 */
function ContactsForm({
  t,
  onMemberChange,
  member,
  countries,
  vatTypes,
  onSkipStep,
}) {
  const { organization } = useContext(ConfigsContext);
  const { requiredFieldsPerBlock = [] } =
    organization?.services?.me?.config || {};
  const [state, setState] = useState(stateFromProps(member));
  useEffect(() => setState(stateFromProps(member)), [member]);

  const [errors, setErrors] = useState({
    phone: false,
    address: false,
    shippingAddress: false,
  });

  const [checked, setChecked] = useState(0);
  const formId = 'contactsForm';

  const validationRules = useMemo(
    () =>
      getValidationRules(requiredFieldsPerBlock, PROFILE_BLOCK.CONTACTS, {}),
    [getValidationRules, requiredFieldsPerBlock],
  );

  // Format address
  const returnAddress = address => {
    const address_2 = address?.address_2 ? `, ${address?.address_2}` : '';
    let value = `${address?.address_1}${address_2}, ${address?.zipCode} ${address?.city}, ${address?.country}`;
    if (!address?.address_1) {
      value = '';
    }
    return value;
  };

  const handleSubmit = async () => {
    const newErrors = getErrors(validationRules, state);

    if (checkIfHasErrors(newErrors)) {
      setErrors(newErrors);
      return;
    }

    const defaultShippingAddress = state.shippingAddress.splice(checked, 1);
    state.shippingAddress.unshift(...defaultShippingAddress);
    let billingAddress = null;
    if (state.address && state.address.address_1 && state.address.countryCode) {
      billingAddress = {
        ...state.address,
        address: returnAddress(state.address),
      };
    }
    const params = {
      address: billingAddress,
      phone: state.phone,
      shippingAddress: state.shippingAddress,
    };
    setChecked(0);
    onMemberChange(params);
  };

  const handleChange = useCallback(
    (field, value) => setState(prevState => ({ ...prevState, [field]: value })),
    [],
  );

  const inputProps = useCallback(() => undefined, []);

  // Modal state
  const [showAddAddress, setShowAddAddress] = useModal({
    show: false,
  });
  const [showRemoveModal, setShowRemoveModal] = useModal({
    show: false,
  });
  // Handling the modal info
  const handleEditAddress = index =>
    setShowAddAddress({ show: true, data: { index } });

  // Remove Address - splicing the array
  const removeAddress = index => {
    setState(prevState => {
      prevState.shippingAddress.splice(index, 1);
      return { ...prevState };
    });
    setShowRemoveModal({ show: false, data: null });
  };

  const { loggedUser } = useContext(LoggedUserContext);
  const billingAddressTitle = validationRules?.address?.required
    ? `${t('profile_address')}*`
    : t('profile_address');
  const shippingAddressTitle = validationRules?.shippingAddress?.required
    ? `${t('profile_shipping_address')}*`
    : t('profile_shipping_address');
  return (
    <>
      {showRemoveModal.show && (
        <RemoveModal
          t={t}
          title={t('profile_remove_title_address')}
          contentText={t('profile_remove_content_address')}
          index={showRemoveModal.data.index}
          onRemove={() => {
            removeAddress(showRemoveModal.data.index);
          }}
          onClose={() => {
            setShowRemoveModal({ show: false, data: null });
          }}
        />
      )}
      {showAddAddress.show && (
        <AddAddressModal
          vatTypes={vatTypes}
          address={
            showAddAddress.data.isBilling
              ? state.address
              : state.shippingAddress[showAddAddress.data.index]
          }
          onClose={async updateState => {
            if (updateState) {
              const saveState = updateState;
              if (!showAddAddress.data.isBilling) {
                setErrors(prevErrors => ({
                  ...prevErrors,
                  shippingAddress: false,
                }));
                setState(prevState => {
                  const { shippingAddress } = prevState;
                  const { index } = showAddAddress.data;
                  shippingAddress[index] = handleShippingAddressInOrder(
                    loggedUser,
                    saveState,
                  );
                  return { ...prevState, shippingAddress };
                });
              } else {
                setErrors(prevErrors => ({ ...prevErrors, address: false }));
                setState(prevState => ({
                  ...prevState,
                  address: saveState,
                }));
              }
              setShowAddAddress({ show: false, data: null });
            } else {
              setShowAddAddress({ show: false, data: null });
            }
          }}
          countries={countries}
          userProfile={member}
          isShippingAddress={!showAddAddress.data.isBilling}
        />
      )}
      <Form
        className="m-form m-form--fit m-form--label-align-right"
        id={formId}
        submitMethod={handleSubmit}
        validationRules={{}}
      >
        {submitHandler => (
          <>
            <div className="m-portlet__body">
              <div className="row profile-skipStep">
                <div className={`${!onSkipStep ? 'col-12' : 'col-6'}`}>
                  <FormSubtitle title={t('profile_tab_contacts')} />
                </div>
                {onSkipStep && (
                  <div className="col-6 skip-wrapper btn-wrapper">
                    <SkipStepButton onClick={onSkipStep} />
                  </div>
                )}
              </div>
              <ProfileField
                field="phone"
                value={state.phone}
                onChange={handleChange}
                inputProps={inputProps}
                block={PROFILE_BLOCK.CONTACTS}
                requiredFieldsPerBlock={requiredFieldsPerBlock}
                labelClassName={`col-lg-2 col-sm-12 ${
                  errors?.phone ? 'label-error' : ''
                }`}
              />
              {errors?.phone && <ErrorMessage />}
              <FormSubtitle title={billingAddressTitle} />
              <div className="row">
                <div className="col-lg-9 col-12">
                  {state?.address?.address_1 && (
                    <FormGroup className="row billing-address">
                      <FormLabel
                        id="address"
                        label={t('profile_address')}
                        className={`col-lg-3 col-12 label-correction ${
                          errors?.address ? 'label-error' : ''
                        }`}
                      />
                      <div className="col-lg-9 col-12 remove-padding display-address">
                        <span>{returnAddress(state?.address) || ''}</span>
                      </div>
                    </FormGroup>
                  )}
                  {!state?.address?.address_1 && (
                    <div className="add-new-btn">
                      <Anchor
                        onClick={() => {
                          setShowAddAddress({
                            show: true,
                            data: { isBilling: true },
                          });
                        }}
                        className="btn btn-outline-metal m-btn m-btn--icon
                             m-btn--icon-only m-btn--custom m-btn--outline-2x
                             m-btn--pill m-btn--air"
                      >
                        <i className="la la-plus" />{' '}
                        <span>{t('profile_add_new_address')}</span>
                      </Anchor>
                    </div>
                  )}
                </div>
                {state?.address?.address_1 && (
                  <div className="col-lg-3 col-12 button-shipping-address padding-container-shipping-bottom">
                    <button
                      type="button"
                      onClick={() => {
                        setShowAddAddress({
                          show: true,
                          data: { isBilling: true },
                        });
                      }}
                      className="btn btn-brand btn-sm"
                    >
                      <i className="la la-pencil" /> {t('profile_edit_address')}
                    </button>
                  </div>
                )}
              </div>
              {errors?.address && <ErrorMessage />}
              <FormSubtitle title={shippingAddressTitle} />
              <div className="row shipping-address">
                {state.shippingAddress.map((address, index) => {
                  const value = returnAddress(address);
                  return (
                    <Fragment key={address?._id}>
                      <div className="col-lg-9 col-12 padding-container-shipping-bottom">
                        <FormGroup className="row mobile-address">
                          <FormLabel
                            id={`shipping_address-${index}`}
                            name={`shipping_address-${index}`}
                            className={`col-lg-3 col-1 label-correction ${
                              errors?.address ? 'label-error' : ''
                            }`}
                            label={
                              // eslint-disable-next-line jsx-a11y/label-has-associated-control,jsx-a11y/label-has-for
                              <label className="m-radio m-radio--state-dynamic">
                                <input
                                  type="radio"
                                  onChange={() => {
                                    setChecked(index);
                                  }}
                                  checked={index === checked}
                                />
                                <span />
                              </label>
                            }
                          />
                          <div className="col-lg-9 col-10 remove-padding display-address">
                            <span>{value}</span>
                          </div>
                        </FormGroup>
                      </div>
                      <div className="col-lg-3 col-12 button-shipping-address padding-container-shipping-bottom">
                        <button
                          type="button"
                          onClick={() => {
                            handleEditAddress(index);
                          }}
                          className="btn btn-brand btn-sm"
                        >
                          <i className="la la-pencil" />{' '}
                          {t('profile_edit_address')}
                        </button>
                        <button
                          type="button"
                          onClick={() => {
                            setShowRemoveModal({ show: true, data: { index } });
                          }}
                          className="btn btn-brand btn-sm"
                        >
                          <i className="la la-trash" />{' '}
                          {t('profile_remove_address')}
                        </button>
                      </div>
                    </Fragment>
                  );
                })}
              </div>
              <div className="add-new-btn">
                <Anchor
                  onClick={() => {
                    handleEditAddress(state.shippingAddress.length);
                  }}
                  className="btn btn-outline-metal m-btn m-btn--icon
                             m-btn--icon-only m-btn--custom m-btn--outline-2x
                             m-btn--pill m-btn--air"
                >
                  <i className="la la-plus" />{' '}
                  <span>{t('profile_add_new_address')}</span>
                </Anchor>
              </div>
              {errors?.shippingAddress && (
                <ErrorMessage
                  styleMessage={{ marginLeft: '-30px', marginTop: '-20px' }}
                />
              )}
            </div>
            <FormFooter submitHandler={submitHandler} />
          </>
        )}
      </Form>
    </>
  );
}

ContactsForm.propTypes = {
  /** translation function */
  t: PropTypes.func.isRequired,
  /** member being edited */
  member: PropTypes.shape(UserProp.propType).isRequired,
  /** member change event handler */
  onMemberChange: PropTypes.func.isRequired,
  countries: PropTypes.arrayOf(PropTypes.shape(CountryProp.propType))
    .isRequired,
  vatTypes: PropTypes.arrayOf(PropTypes.shape(VatTypesProp.propType))
    .isRequired,
  onSkipStep: PropTypes.func,
};

ContactsForm.defaultProps = {
  onSkipStep: null,
};

export default translate(ContactsForm);
